<template>
  <!-- Persistent online-strip that hugs the LEFT edge of the chat panel.
       Desktop-only (the panel becomes a FAB on mobile). Space for it is
       reserved globally via STRIP_W folded into --chat-panel-width, so this
       must NOT cause the panel <aside> to gain overflow-hidden. -->
  <aside
    class="chat-online-strip absolute top-0 right-full h-full w-[52px] hidden md:flex flex-col items-center"
    :aria-label="$t('chat.online.title')"
  >
    <!-- Header: live count -->
    <button
      type="button"
      class="w-full shrink-0 flex flex-col items-center gap-0.5 py-2 text-text-dim hover:text-white transition-colors"
      :title="$t('chat.online.count', { n: list.length })"
      :aria-label="$t('chat.online.title')"
      @click="openPanel"
    >
      <span class="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
      <span class="text-[10px] tabular-nums font-semibold leading-none">{{ list.length }}</span>
    </button>

    <div class="w-full border-b border-white/5"></div>

    <!-- Avatars -->
    <div class="flex-1 min-h-0 w-full overflow-y-auto scrollbar-thin flex flex-col items-center gap-1.5 py-2">
      <button
        v-for="u in list"
        :key="u.id"
        type="button"
        class="relative shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50"
        :class="{ 'collab-glow': u.openForCollabs }"
        :aria-label="u.name"
        @mouseenter="onEnter(u.id, $event)"
        @mouseleave="scheduleHide"
        @focus="onEnter(u.id, $event)"
        @blur="scheduleHide"
        @click="openPanel"
      >
        <img
          :src="u.avatar || '/images/default-avatar.svg'"
          :alt="u.name"
          class="w-9 h-9 rounded-full object-cover"
          :class="u.openForCollabs ? 'ring-2 ring-cyan-400/70 ring-offset-1 ring-offset-transparent' : ''"
        />
        <span
          class="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-[var(--chrome-bg,#0d0e12)]"
          :class="dotClass(u.status)"
        ></span>
      </button>

      <div v-if="list.length === 0" class="px-1 pt-2 text-[9px] text-text-dim text-center leading-tight">
        <span class="block w-1.5 h-1.5 rounded-full bg-white/15 mx-auto mb-1"></span>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { usePresence } from '~/composables/chat/usePresence'
import { useHoverCard } from '~/composables/chat/useHoverCard'
import { useChatUI } from '~/composables/chat/useChatUI'

const { onlineExcludingSelf: list } = usePresence()
const { show, scheduleHide } = useHoverCard()
const { setTab } = useChatUI()

function onEnter(userId, e) {
  const el = e.currentTarget
  if (!el) return
  show(userId, el.getBoundingClientRect())
}

function openPanel() {
  setTab('online') // also opens the panel (setTab sets isOpen = true)
}

function dotClass(status) {
  if (status === 'afk') return 'bg-amber-400'
  if (status === 'dnd') return 'bg-red-500'
  return 'bg-emerald-400'
}
</script>

<style scoped>
/* Glass chrome — same tokens as the panel/sidebars so the strip reads as one
   continuous frame with the chat panel to its right. */
.chat-online-strip {
  background: var(--chrome-bg, rgba(13, 14, 18, 0.65));
  backdrop-filter: blur(var(--chrome-blur, 24px));
  -webkit-backdrop-filter: blur(var(--chrome-blur, 24px));
  border-left: 1px solid var(--chrome-border, rgba(255, 255, 255, 0.06));
}

.scrollbar-thin::-webkit-scrollbar { width: 4px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.08); border-radius: 3px; }
.scrollbar-thin::-webkit-scrollbar-track { background: transparent; }

.collab-glow { animation: collabGlow 2.8s ease-in-out infinite; border-radius: 9999px; }
@keyframes collabGlow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 242, 255, 0.28); }
  50%      { box-shadow: 0 0 0 6px rgba(0, 242, 255, 0.00); }
}
</style>
